/**
 * 发卡（卡牌只由家长发，docs/成就系统.md 第 3 节，与 iOS GrantCardSheet 一致）：
 * 现写一张故事卡，或从图鉴里选一张 / 随机抽。每次发卡都是一条成长记录：哪天的事、发生了什么、照片，可以顺便加分、关联一条成就。
 * 从「待确认」进来时（checkin 不为空）先确认这次完成，再把卡和这次完成关联起来。
 */
import { useState } from "react";
import { api, type Card, type Checkin, type Child, type ChildCard, didWrite, run, select, shallowEqual, toast, upsert, useCalendar, useStore, writeEntity } from "../../core";
import { C, CardFace, ChoiceChip, FormCard, ModalHeader, NumberStepper, PillSegment, RowDivider, Sheet, SheetBody, TextArea, TextField, ValueRow } from "../../ui";
import { rarityStyle } from "../../ui/styles";
import { CardArtImage, CardPhotosEditor } from "./CardPhotos";
import "./cards.css";

const GLYPHS = ["⭐", "🏆", "🚲", "📚", "✏️", "🎨", "🎹", "⚽", "🌱", "💪", "🧹", "❤️"];
const RARITIES = ["common", "rare", "epic", "legendary"] as const;

export function GrantCardSheet({ child, checkin, approvePoints, open, onClose }: { child: Child | null; checkin?: Checkin | null; approvePoints?: number; open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open && !!child} onClose={onClose} width={560} persistent label="发卡">
      {open && child && <GrantForm key={checkin?.id ?? child.id} child={child} checkin={checkin ?? null} approvePoints={approvePoints} onClose={onClose} />}
    </Sheet>
  );
}

function GrantForm({ child, checkin, approvePoints, onClose }: { child: Child; checkin: Checkin | null; approvePoints?: number; onClose: () => void }) {
  const cal = useCalendar();
  const series = useStore(select.collectionSeries, shallowEqual);
  const cardsById = useStore((s) => s.cards);
  const owned = useStore((s) => select.ownedCardCounts(s, child.id), shallowEqual);
  const rulesById = useStore((s) => s.achievementRules);
  const [mode, setMode] = useState<"story" | "collection">("story");
  const [title, setTitle] = useState(checkin ? checkin.title.slice(0, 20) : "");
  const [glyph, setGlyph] = useState("");
  const [rarity, setRarity] = useState<string>("rare");
  const [seriesId, setSeriesId] = useState<string | null>(series[0]?.id ?? null);
  const [cardId, setCardId] = useState<string | null>(null);
  const [story, setStory] = useState("");
  const [date, setDate] = useState(checkin?.occurrenceDate ?? cal.today());
  const [points, setPoints] = useState(0);
  const [ruleId, setRuleId] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const name = title.trim();
  const face = glyph || [...name][0] || "?";
  const canSubmit = (mode === "story" ? !!name : series.length > 0) && !uploading;
  const cards = seriesId ? Object.values(cardsById).filter((c) => c.seriesId === seriesId).sort((a, b) => a.no - b.no) : [];
  const rules = Object.values(rulesById).sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));

  const submit = async () => {
    setBusy(true);
    const s = story.trim();
    const body: Record<string, unknown> = { story: s || undefined, occurredOn: date, points, photoAssetIds: photos };
    if (checkin) body.checkinId = checkin.id;
    else if (ruleId) body.achievementRuleId = ruleId;
    if (mode === "story") body.newCard = { name, glyph: glyph || undefined, rarity, imagePrompt: [name, s].filter(Boolean).join("。") };
    else if (cardId) body.cardId = cardId;
    else body.seriesId = seriesId ?? undefined;
    const r = await run(async () => {
      if (checkin?.status === "pending") await writeEntity("checkins", "POST", `checkins/${checkin.id}/decide`, { decision: "approve", points: approvePoints ?? checkin.points });
      const res = await api.post<{ childCard: ChildCard; card: Card }>(`children/${child.id}/cards/grant`, body);
      upsert("cards", res.card);
      didWrite("childCards", res.childCard);
      return res;
    });
    setBusy(false);
    if (r) {
      toast(`已发给${child.name}「${r.card.name}」`);
      onClose();
    }
  };

  return (
    <>
      <ModalHeader title={checkin ? "确认并发卡" : `给${child.name}发卡`} actionTitle="发卡" actionEnabled={canSubmit} busy={busy} onCancel={onClose} onAction={() => void submit()} />
      <SheetBody gap={12}>
        <PillSegment items={[["story", "写一张故事卡"], ["collection", "从图鉴选"]] as const} value={mode} onChange={setMode} height={36} stretch />
        {mode === "story" ? (
          <>
            <div className="kp-grant-story">
              <div className="kp-grant-face">
                <CardFace glyph={face} name={name || "卡片标题"} rarity={rarity} />
              </div>
              <div className="kp-grant-fields">
                <TextField label="标题" value={title} onChange={(v) => setTitle(v.slice(0, 20))} placeholder="例如：第一次自己骑车" data-testid="card-title" />
                <span className="kp-grant-label">稀有度</span>
                <div className="kp-grant-chips">
                  {RARITIES.map((r) => (
                    <ChoiceChip key={r} selected={rarity === r} unselectedBg={C.bg} onClick={() => setRarity(r)}>
                      {rarityStyle(r).name}
                    </ChoiceChip>
                  ))}
                </div>
              </div>
            </div>
            <FormCard>
              <div className="kp-grant-block">
                <span className="kp-grant-label">卡面</span>
                <div className="kp-grant-chips">
                  <ChoiceChip selected={!glyph} unselectedBg={C.bg} onClick={() => setGlyph("")}>
                    标题首字
                  </ChoiceChip>
                  {GLYPHS.map((g) => (
                    <ChoiceChip key={g} selected={glyph === g} unselectedBg={C.bg} onClick={() => setGlyph(g)}>
                      {g}
                    </ChoiceChip>
                  ))}
                </div>
                <small className="kp-grant-note">没有卡面插画时，卡上显示这个字或表情。卡面插画要在家里的 Wi-Fi 下用 iPhone 家长端生成：发卡时生成，或者让语音助手给已经发出的卡生成。</small>
              </div>
            </FormCard>
          </>
        ) : (
          <div className="kp-grant-collection">
            <div className="kp-grant-chips">
              {series.map((s) => (
                <ChoiceChip key={s.id} selected={s.id === seriesId} unselectedBg={C.surface} onClick={() => (setSeriesId(s.id), setCardId(null))}>
                  {s.name}
                </ChoiceChip>
              ))}
            </div>
            <ChoiceChip selected={cardId == null} unselectedBg={C.surface} onClick={() => setCardId(null)}>
              随机抽一张（优先没有的）
            </ChoiceChip>
            <div className="kp-grant-cards">
              {cards.map((c) => {
                const n = owned[c.id] ?? 0;
                return (
                  <button key={c.id} type="button" className={`kp-grant-card ${cardId === c.id ? "is-on" : ""} ${n > 0 ? "" : "is-missing"}`} onClick={() => setCardId(c.id)}>
                    <CardFace glyph={c.glyph} name={c.name} rarity={c.rarity} count={n} art={c.imageAssetId ? <CardArtImage assetId={c.imageAssetId} /> : undefined} />
                  </button>
                );
              })}
            </div>
            <small className="kp-grant-note">淡色的是{child.name}还没有的卡</small>
          </div>
        )}
        <FormCard>
          <div className="kp-grant-block">
            <TextArea label="发生了什么" value={story} onChange={(v) => setStory(v.slice(0, 500))} placeholder="写下这件事，以后翻卡册时能想起来" rows={3} />
          </div>
          <RowDivider />
          <ValueRow title="哪天的事">
            <input type="date" className="kp-grant-date" value={date} max={cal.today()} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </ValueRow>
          <RowDivider />
          <ValueRow title="顺便加分" height={56}>
            <NumberStepper value={points} min={0} max={100} step={5} onChange={setPoints} />
          </ValueRow>
          {!checkin && rules.length > 0 && (
            <>
              <RowDivider />
              <ValueRow title="对应成就">
                <select className="kp-grant-select" value={ruleId} onChange={(e) => setRuleId(e.target.value)} aria-label="对应成就">
                  <option value="">不关联</option>
                  {rules.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </ValueRow>
            </>
          )}
        </FormCard>
        <FormCard>
          <div className="kp-grant-block">
            <CardPhotosEditor value={photos} onChange={setPhotos} onUploading={setUploading} />
          </div>
        </FormCard>
        {checkin && (
          <p className="kp-grant-note">
            会先确认「{checkin.title}」（+{(approvePoints ?? checkin.points) + (checkin.bonusPoints ?? 0)} 分），再发这张卡。
          </p>
        )}
      </SheetBody>
    </>
  );
}
